import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Spin } from 'antd';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import AuthCallback from './pages/AuthCallback';
import { usePermissions } from './hooks/usePermissions';

/**
 * Root component and router for the admin SPA.
 *
 * Everything is behind <ProtectedRoute/>, which redirects unauthenticated
 * visitors to the SWA login endpoint. The /login path is a convenience
 * alias that performs the same redirect. Pages are lazy-loaded so each
 * route's code (and page-only deps such as recharts) is a separate
 * chunk — see docs/plans/admin-site-proposal.md, section 10 (bundle
 * size mitigation).
 *
 * State persistence across sidebar navigation: Dashboard, BI, and
 * Restaurants are "persistent" pages. Their routes render `element={null}`
 * — the actual page components live in <PersistentPages/>, which is
 * rendered by Layout (always mounted). Navigating between them (or to
 * any other page) only toggles `display: none`, so their in-memory state
 * (search filters, fetched results, pagination, scroll) survives.
 * DataManagement is intentionally not persistent: it must remount so a
 * new deep link renders a different restaurant.
 */

// Page components are code-split at the route level.
const Verification = lazy(() => import('./pages/Verification'));
const DataManagement = lazy(() => import('./pages/DataManagement'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));

const pageFallback = (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 240,
    }}
  >
    <Spin />
  </div>
);

function PermissionRoute({ action, children }) {
  const { can } = usePermissions();
  return can(action) ? children : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <Suspense fallback={pageFallback}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            {/* Persistent pages: rendered by <PersistentPages/> inside
                Layout; the routes only own the URL. */}
            <Route index element={null} />
            <Route path="/bi" element={null} />
            <Route
              path="/restaurants"
              element={<PermissionRoute action="viewRestaurants">{null}</PermissionRoute>}
            />
            <Route
              path="/notifications"
              element={<PermissionRoute action="viewNotifications">{null}</PermissionRoute>}
            />
            <Route path="/verification" element={<Verification />} />
            <Route
              path="/users"
              element={<PermissionRoute action="viewUsers">{null}</PermissionRoute>}
            />
            <Route
              path="/onboarding"
              element={<PermissionRoute action="onboarding">{null}</PermissionRoute>}
            />
            <Route
              path="/tools"
              element={<PermissionRoute action="storageHygiene">{null}</PermissionRoute>}
            />
            <Route
              path="/data-management"
              element={<PermissionRoute action="viewRestaurantDetails"><DataManagement /></PermissionRoute>}
            />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
